import { useMemo } from 'react';
import { Menu, MenuList, Button, MenuOptionGroup, MenuButton } from '@chakra-ui/react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StatusTag from '@/components/StatusTag';
import { trioUITheme } from '@/styles/theme';
import { OptionsWithLabel } from '@/types';
import * as Styles from './DropdownGroup.styles';
import WarningMessage from '../WarningMessage';

export type DropdownGroupProps = {
  label: string;
  name: string;
  defaultValue: string | string[] | undefined;
  onChangeDropDown: (e) => void;
  dropdownGroups: OptionsWithLabel[];
  isMultiple?: boolean;
  errorMessage?: string;
  isDisabled?: boolean;
  isLabelAboveField?: boolean;
  placeholder?: string;
};

const DropdownGroup = ({
  label,
  name,
  defaultValue,
  onChangeDropDown,
  dropdownGroups,
  isMultiple,
  errorMessage,
  isDisabled,
  placeholder = 'Select an option',
  isLabelAboveField,
  ...props
}: DropdownGroupProps): JSX.Element => {
  const formattedValue = useMemo(() => {
    if (typeof defaultValue === 'string') {
      return defaultValue;
    }

    if (typeof defaultValue === 'boolean') {
      return defaultValue === true ? 'Yes' : 'No';
    }

    if (!defaultValue) {
      return null;
    }

    const valuesFormatted = defaultValue.filter(Boolean).join(', ');

    return valuesFormatted.toLowerCase();
  }, [defaultValue]);

  return (
    <Styles.InputBox
      data-testid={`dropdown-${name}`}
      isLabelAboveField={isLabelAboveField}
      {...props}
    >
      <Styles.InputLabel htmlFor={name} isLabelAboveField={isLabelAboveField}>
        {label}
      </Styles.InputLabel>
      <Styles.InputArea isLabelAboveField={isLabelAboveField}>
        <Menu closeOnSelect={!isMultiple}>
          {({ isOpen }) => (
            <div className={`dropdown-is-${isOpen ? 'opened' : 'closed'}`}>
              <MenuButton
                height="32px"
                width="100%"
                borderRadius={4}
                textAlign="left"
                backgroundColor={trioUITheme.colors['neutral-30']}
                borderWidth={1}
                borderColor={trioUITheme.colors['neutral-40']}
                disabled={isDisabled}
                fontWeight="normal"
                overflow="hidden"
                data-testid={`dropdown-${name}-btn`}
                rightIcon={
                  <ExpandMoreIcon
                    style={{ fill: trioUITheme.colors['neutral-100'], fontSize: '12px' }}
                  />
                }
                as={Button}
                padding="5.5px 12px"
              >
                {formattedValue ? <Styles.MenuTag status={formattedValue} colored /> : placeholder}
              </MenuButton>

              <MenuList
                minW="100%"
                w="100%"
                boxShadow="0px 4px 6px 0px rgba(0, 0, 0, 0.07);"
                backgroundColor={trioUITheme.colors['neutral-20']}
                borderColor={trioUITheme.colors['neutral-40']}
                maxH={300}
                sx={{ overflowY: 'scroll' }}
              >
                {dropdownGroups.map((dropdownGroup) => {
                  return (
                    <MenuOptionGroup
                      key={dropdownGroup.label}
                      title={dropdownGroup.label}
                      color={trioUITheme.colors['neutral-80']}
                      fontSize="9px"
                      lineHeight="10px"
                      padding="10px 11px 11px"
                      textTransform="uppercase"
                      margin="0"
                      value={defaultValue}
                      data-testid={`dropdown-${name}`}
                      onChange={(value) => {
                        return onChangeDropDown({
                          target: { value, id: name, name },
                          type: 'change',
                        });
                      }}
                      type="radio"
                    >
                      {dropdownGroup.options.map((option, index) => {
                        return (
                          <Styles.MenuOption
                            fontSize="14px"
                            lineHeight="21px"
                            padding="6px 12px 0"
                            backgroundColor={trioUITheme.colors['neutral-20']}
                            data-testid={`dropdown-${name}-option-${index}`}
                            value={option}
                            key={option}
                          >
                            <StatusTag outlined status={option} />
                          </Styles.MenuOption>
                        );
                      })}
                    </MenuOptionGroup>
                  );
                })}
              </MenuList>
            </div>
          )}
        </Menu>
        <WarningMessage errorMessage={errorMessage} />
      </Styles.InputArea>
    </Styles.InputBox>
  );
};

export default DropdownGroup;
